import { useAuthViewport } from '@/epics/authentication/pages/login/AuthTemplate/useAuthViewport';
import { useState, useMemo, useCallback } from 'react';

interface UseLayoutReturn {
  // State
  isCollapsed: boolean;

  // Functions
  toggleCollapse: () => void;
  setCollapsed: (collapsed: boolean) => void;

  // Computed values
  sidebarWidth: string;
  logoSrc: string;
  logoTransform: string;

  // Style objects
  sidebarStyle: React.CSSProperties;
  headerStyle: React.CSSProperties;
  contentAreaStyle: React.CSSProperties;
  logoStyle: React.CSSProperties;
}

const COLLAPSED_WIDTH = '88px';
const EXPANDED_WIDTH = '220px';
const LOGO_HEIGHT = '50px';
const LABEL_HEIGHT = '56px';
const HEADER_HEIGHT = '64px';

export const useLayout = (): UseLayoutReturn => {
  const { isTablet } = useAuthViewport();
  const [isCollapsed, setIsCollapsed] = useState(isTablet ? true : false);

  // Functions
  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const setCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsed(collapsed);
  }, []);

  // Computed values
  const sidebarWidth = useMemo(
    () => (isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH),
    [isCollapsed]
  );

  const logoSrc = useMemo(
    () => (isCollapsed ? '/collapsedLogo.svg' : '/pozytronLogo.svg'),
    [isCollapsed]
  );

  const logoTransform = useMemo(
    () => (isCollapsed ? 'scale(1.5)' : 'scale(1)'),
    [isCollapsed]
  );

  // Style objects
  const sidebarStyle = useMemo<React.CSSProperties>(
    () => ({
      width: sidebarWidth,
    }),
    [sidebarWidth]
  );

  const headerStyle = useMemo<React.CSSProperties>(
    () => ({
      marginLeft: sidebarWidth,
      width: `calc(100vw - ${sidebarWidth})`,
    }),
    [sidebarWidth]
  );

  const contentAreaStyle = useMemo<React.CSSProperties>(
    () => ({
      marginLeft: sidebarWidth,
      width: `calc(100vw - ${sidebarWidth})`,
    }),
    [sidebarWidth]
  );

  const logoStyle = useMemo<React.CSSProperties>(
    () => ({
      height: LOGO_HEIGHT,
      width: '100%',
      objectFit: 'contain' as const,
      transform: logoTransform,
    }),
    [logoTransform]
  );

  return {
    // State
    isCollapsed,

    // Functions
    toggleCollapse,
    setCollapsed,

    // Computed values
    sidebarWidth,
    logoSrc,
    logoTransform,

    // Style objects
    sidebarStyle,
    headerStyle,
    contentAreaStyle,
    logoStyle,
  };
};

// Export constants for use in component if needed
export const LAYOUT_CONSTANTS = {
  COLLAPSED_WIDTH,
  EXPANDED_WIDTH,
  LOGO_HEIGHT,
  LABEL_HEIGHT,
  HEADER_HEIGHT,
};
